/**
 * Professional Form Component
 * Reusable form for creating and editing Professional data with sequential tabs
 */
import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  FormHelperText,
  Alert,
  FormControlLabel,
  Checkbox,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {
  Professional,
  ProfessionalType,
  PROFESSIONAL_TYPE_LABELS,
  isEngineerProfessionalType,
  CONSELHOS_PROFISSIONAIS,
  conselhoDoTipo,
  ConselhoKey,
  ConselhoProfissional,
  CreateProfessionalRequest,
  UpdateProfessionalRequest
} from '../../../types/professional';
import { professionalService } from '../../../services/professionalService';

// Utility functions for formatting
const formatCPF = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

const removeCPFMask = (value: string): string => value.replace(/\D/g, '');

const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

// O `formatCRM`, que montava "123456/SP" dentro do próprio campo, saiu em #345:
// o número do conselho guarda SÓ o número. Ver o comentário do seletor de UF.

// CNPJ formatting: XX.XXX.XXX/XXXX-XX
const formatCNPJ = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

// Binding function labels
const BINDING_FUNCTION_OPTIONS = [
  { value: 'doctor', label: 'Médico' },
  { value: 'technician', label: 'Técnico' },
  { value: 'consultant', label: 'Consultor' },
  { value: 'coordinator', label: 'Coordenador' },
  { value: 'employee', label: 'Funcionário' },
  { value: 'manager', label: 'Gestor' },
  { value: 'director', label: 'Diretor' },
  { value: 'admin', label: 'Administrador' },
];

/** As chaves dos conselhos, na ordem de fallback compartilhada com o backend. */
const CHAVES_DE_CONSELHO: readonly ConselhoKey[] = CONSELHOS_PROFISSIONAIS.map((c) => c.chave);

/**
 * Os registros que a validação EXIGE quando são o conselho do tipo selecionado
 * — espelha os `when('professionalType')` do `validationSchema`. Os demais são
 * sempre opcionais: um profissional pode ter conselho diferente do que o tipo
 * sugere (ex.: ergonomista que é engenheiro).
 */
const CONSELHOS_OBRIGATORIOS: readonly ConselhoKey[] = ['crm', 'crea', 'mtb'];

/** MTB é registro ministerial: não tem UF de conselho. */
const CHAVES_COM_UF: readonly ConselhoKey[] = CHAVES_DE_CONSELHO.filter((chave) => chave !== 'mtb');

// Tab steps configuration
const TAB_STEPS = [
  { label: 'Dados Básicos', value: 0 },
  { label: 'Registros Profissionais', value: 1 },
  { label: 'Responsabilidades', value: 2 },
  { label: 'Vínculos com Clínicas', value: 3 },
  { label: 'Dados Bancários', value: 4 },
];

// Validation schema
/**
 * Cadastro antigo pode ter tipo fora da lista fechada — a API só passou a
 * recusar isso na #423. Sem normalizar, o campo receberia um valor sem opção
 * correspondente: ficaria em branco SEM o "Selecione o tipo", e a recusa sairia
 * como "Tipo inválido" em vez de dizer que falta preencher.
 */
const tipoConhecido = (tipo: string | undefined): string =>
  tipo !== undefined && (Object.values(ProfessionalType) as string[]).includes(tipo) ? tipo : '';

const validationSchema = yup.object({
  name: yup.string().required('Nome é obrigatório').min(2, 'Mínimo de 2 caracteres').max(255, 'Máximo de 255 caracteres'),
  email: yup.string().required('Email é obrigatório').email('Email inválido').max(255, 'Máximo de 255 caracteres'),
  cpf: yup
    .string()
    .required('CPF é obrigatório')
    .test('cpf-format', 'CPF deve conter 11 dígitos', (value) => {
      if (!value) return false;
      const numericOnly = value.replace(/\D/g, '');
      return numericOnly.length === 11;
    }),
  password: yup.string().when('$isEditing', {
    is: false,
    then: (schema) => schema.required('Senha é obrigatória').min(6, 'Mínimo de 6 caracteres'),
    otherwise: (schema) => schema.optional(),
  }),
  whatsapp: yup.string().nullable().transform(value => value === '' ? null : value),
  // O vazio entra na lista de propósito: sem ele quem reprova o campo em branco é o
  // `oneOf`, e o usuário lê "Tipo inválido" no lugar de "é obrigatório".
  professionalType: yup.string().oneOf([...Object.values(ProfessionalType) as string[], ''], 'Tipo inválido').required('Tipo de Profissional é obrigatório'),
  
  // Registrations - conditional based on professionalType
  crm: yup.string().when('professionalType', {
    is: (type: string) => [ProfessionalType.MEDICO_TRABALHO, ProfessionalType.MEDICO_EXAMINADOR].includes(type as ProfessionalType),
    then: (schema) => schema.required('CRM é obrigatório para Médicos'),
    otherwise: (schema) => schema.nullable().transform(value => value === '' ? null : value),
  }),
  rqe: yup.string().nullable().transform(value => value === '' ? null : value),
  crea: yup.string().when('professionalType', {
    is: (type: string) => isEngineerProfessionalType(type),
    then: (schema) => schema.required('CREA é obrigatório para Engenheiros'),
    otherwise: (schema) => schema.nullable().transform(value => value === '' ? null : value),
  }),
  cro: yup.string().nullable().transform(value => value === '' ? null : value),
  cra: yup.string().nullable().transform(value => value === '' ? null : value),
  mtb: yup.string().when('professionalType', {
    is: ProfessionalType.TECNICO_SEGURANCA,
    then: (schema) => schema.required('MTB é obrigatório para Técnicos de Segurança'),
    otherwise: (schema) => schema.nullable().transform(value => value === '' ? null : value),
  }),
  crfa: yup.string().nullable().transform(value => value === '' ? null : value),
  crefito: yup.string().nullable().transform(value => value === '' ? null : value),
  crq: yup.string().nullable().transform(value => value === '' ? null : value),
  crp: yup.string().nullable().transform(value => value === '' ? null : value),
  coren: yup.string().nullable().transform(value => value === '' ? null : value),
  conselhoUf: yup.string().when('professionalType', {
    is: (type: string) =>
      [ProfessionalType.MEDICO_TRABALHO, ProfessionalType.MEDICO_EXAMINADOR].includes(type as ProfessionalType) ||
      isEngineerProfessionalType(type),
    then: (schema) => schema.required('UF do Conselho é obrigatória'),
    otherwise: (schema) => schema.nullable().transform(value => value === '' ? null : value),
  }),
  specialty: yup.string().nullable().transform(value => value === '' ? null : value),
  defaultAppointmentDuration: yup.number().nullable().transform((value, originalValue) => originalValue === '' ? null : value).oneOf([null, 15, 20, 25, 30, 40, 45, 60, 90, 120] as any[], 'Duração inválida'),

  // Responsibilities
  isExaminer: yup.boolean().default(false),
  isCoordinator: yup.boolean().default(false),
  isMedicalResponsible: yup.boolean().default(false),
  isTechnicalResponsible: yup.boolean().default(false),
  isEnvironmentalResponsible: yup.boolean().default(false),
  isLegalResponsible: yup.boolean().default(false),
  
  // Bank data
  bankName: yup.string().nullable().transform(value => value === '' ? null : value),
  bankAgency: yup.string().nullable().transform(value => value === '' ? null : value),
  bankAccount: yup.string().nullable().transform(value => value === '' ? null : value),
  bankAccountType: yup.string().nullable().transform(value => value === '' ? null : value),
  pixKeyType: yup.string().nullable().transform(value => value === '' ? null : value),
  pixKey: yup.string().nullable().transform(value => value === '' ? null : value),
  pis: yup.string().nullable().transform(value => value === '' ? null : value),
  
  // Company data
  companyName: yup.string().nullable().transform(value => value === '' ? null : value),
  companyCnpj: yup.string().nullable().transform(value => value === '' ? null : value),
  
  // Other
  observations: yup.string().nullable().transform(value => value === '' ? null : value),
});

interface ProfessionalFormProps {
  initialData?: Professional;
  onSubmit: (data: CreateProfessionalRequest | UpdateProfessionalRequest) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

const ProfessionalForm: React.FC<ProfessionalFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting
}) => {
  const [currentTab, setCurrentTab] = useState(0);
  const isEditing = !!initialData;

  // Provider bindings state
  const [availableProviders, setAvailableProviders] = useState<Array<{ id: number; tradeName: string; legalName: string }>>([]);
  const [providerBindings, setProviderBindings] = useState<Array<{ providerId: number; providerName?: string; function: string; checked: boolean }>>(() => {
    // Initialize from existing entity relationships if editing
    if (initialData && (initialData as any).entityRelationships) {
      const relationships = (initialData as any).entityRelationships as Array<{
        entityType: string;
        entityId: number;
        function: string;
      }>;
      return relationships
        .filter(r => r.entityType === 'provider')
        .map(r => ({
          providerId: Number(r.entityId),
          function: r.function || 'doctor',
          checked: true,
        }));
    }
    return [];
  });
  const [providersLoading, setProvidersLoading] = useState(false);

  // Load providers when tab 3 is activated
  React.useEffect(() => {
    if (currentTab === 3 && availableProviders.length === 0) {
      setProvidersLoading(true);
      professionalService.getProvidersForBinding()
        .then(providers => {
          setAvailableProviders(providers);
          // Mark existing bindings in the providers list
          if (providerBindings.length > 0) {
            const existingIds = new Set(providerBindings.map(b => b.providerId));
            // Don't reset existing bindings, just ensure all providers are visible
            const newBindingEntries = providers
              .filter(p => !existingIds.has(p.id))
              .map(p => ({ providerId: p.id, providerName: p.tradeName, function: 'doctor', checked: false }));
            setProviderBindings(prev => [...prev, ...newBindingEntries]);
          } else {
            setProviderBindings(providers.map(p => ({
              providerId: p.id,
              providerName: p.tradeName,
              function: 'doctor',
              checked: false,
            })));
          }
        })
        .catch(() => {/* ignore errors */})
        .finally(() => setProvidersLoading(false));
    }
  }, [currentTab, availableProviders.length]);

  // Helper to get meta value (handles 'true'/'false' strings)
  const getMetaValue = (value: any, defaultValue: any = '') => {
    if (value === undefined || value === null) return defaultValue;
    if (typeof value === 'boolean') return value;
    if (value === 'true') return true;
    if (value === 'false') return false;
    return value;
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    trigger,
  } = useForm<CreateProfessionalRequest>({
    resolver: yupResolver(validationSchema as any),
    context: { isEditing },
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: initialData ? {
      name: initialData.name,
      email: initialData.email,
      cpf: formatCPF(initialData.cpf),
      whatsapp: initialData.whatsapp ? formatPhone(initialData.whatsapp.replace('+55', '')) : '',
      professionalType: tipoConhecido(initialData.meta?.professionalType),
      crm: initialData.meta?.crm || '',
      rqe: initialData.meta?.rqe || '',
      crea: initialData.meta?.crea || '',
      cro: initialData.meta?.cro || '',
      cra: initialData.meta?.cra || '',
      mtb: initialData.meta?.mtb || '',
      crfa: initialData.meta?.crfa ?? '',
      crefito: initialData.meta?.crefito ?? '',
      crq: initialData.meta?.crq ?? '',
      crp: initialData.meta?.crp ?? '',
      coren: initialData.meta?.coren ?? '',
      conselhoUf: initialData.meta?.conselhoUf || '',
      specialty: initialData.meta?.specialty || '',
      defaultAppointmentDuration: initialData.meta?.defaultAppointmentDuration ? Number(initialData.meta.defaultAppointmentDuration) : ('' as any),
      isExaminer: getMetaValue(initialData.meta?.isExaminer, false),
      isCoordinator: getMetaValue(initialData.meta?.isCoordinator, false),
      isMedicalResponsible: getMetaValue(initialData.meta?.isMedicalResponsible, false),
      isTechnicalResponsible: getMetaValue(initialData.meta?.isTechnicalResponsible, false),
      isEnvironmentalResponsible: getMetaValue(initialData.meta?.isEnvironmentalResponsible, false),
      isLegalResponsible: getMetaValue(initialData.meta?.isLegalResponsible, false),
      bankName: initialData.meta?.bankName || '',
      bankAgency: initialData.meta?.bankAgency || '',
      bankAccount: initialData.meta?.bankAccount || '',
      bankAccountType: initialData.meta?.bankAccountType || '',
      pixKeyType: initialData.meta?.pixKeyType || '',
      pixKey: initialData.meta?.pixKey || '',
      pis: initialData.meta?.pis || '',
      companyName: initialData.meta?.companyName || '',
      companyCnpj: initialData.meta?.companyCnpj || '',
      observations: initialData.meta?.observations || '',
    } : {
      name: '',
      email: '',
      cpf: '',
      password: '',
      whatsapp: '',
      professionalType: '',
      isExaminer: false,
      isCoordinator: false,
      isMedicalResponsible: false,
      isTechnicalResponsible: false,
      isEnvironmentalResponsible: false,
      isLegalResponsible: false,
    },
  });

  const watchProfessionalType = watch('professionalType');

  // A etapa "Vínculos com Clínicas" (value 3) só se aplica a Médico Examinador
  const isExaminer = watchProfessionalType === ProfessionalType.MEDICO_EXAMINADOR;
  const visibleSteps = TAB_STEPS.filter((s) => s.value !== 3 || isExaminer);

  // Determine which registration fields to show based on professional type
  const showCrmFields = [ProfessionalType.MEDICO_TRABALHO, ProfessionalType.MEDICO_EXAMINADOR].includes(watchProfessionalType as ProfessionalType);
  const showCreaFields = isEngineerProfessionalType(watchProfessionalType);

  /**
   * O conselho do TIPO selecionado — mesmo mapa que a listagem e o resolver do
   * backend usam para decidir qual registro sai impresso. Ele ganha destaque; os
   * outros nove ficam atrás de "Outro conselho", sem sair do alcance: os dez
   * continuam preenchíveis porque os signatários são de várias categorias e o
   * profissional pode ter conselho diferente do que o tipo sugere.
   */
  const conselhoDestacado = conselhoDoTipo(watchProfessionalType);
  const outrosConselhos = CONSELHOS_PROFISSIONAIS.filter(
    (conselho) => conselho.chave !== conselhoDestacado?.chave
  );

  const valoresDosConselhos = watch(CHAVES_DE_CONSELHO);
  const conselhoPreenchido = (chave: ConselhoKey): boolean => {
    const valor = valoresDosConselhos[CHAVES_DE_CONSELHO.indexOf(chave)];
    return typeof valor === 'string' && valor.trim() !== '';
  };

  // Recolher um campo já preenchido esconderia dado do usuário: se o registro
  // que ele tem não é o do tipo (o ergonomista que é engenheiro), a seção abre.
  // Sem conselho para o tipo (`OUTRO`), abre também — senão não sobra campo.
  const [outrosConselhosAlternados, setOutrosConselhosAlternados] = useState<boolean | null>(null);
  const outrosConselhosAbertos =
    outrosConselhosAlternados ??
    (!conselhoDestacado || outrosConselhos.some((conselho) => conselhoPreenchido(conselho.chave)));

  // A UF do conselho segue o PREENCHIMENTO, não o tipo: quem digitou qualquer
  // conselho precisa dizer de que UF é o registro. Continua OPCIONAL para eles
  // — só CRM e CREA a exigem (ver `validationSchema`).
  const showConselhoUf =
    showCrmFields || showCreaFields || CHAVES_COM_UF.some((chave) => conselhoPreenchido(chave));

  /**
   * Um campo de conselho. Os dez são iguais no essencial — o que muda é o
   * rótulo, se a validação o exige (só quando é o conselho do tipo) e o MTB,
   * que é registro ministerial e não tem formato de conselho.
   */
  const renderCampoDeConselho = (conselho: ConselhoProfissional) => {
    const ehMtb = conselho.chave === 'mtb';
    const obrigatorio =
      conselho.chave === conselhoDestacado?.chave && CONSELHOS_OBRIGATORIOS.includes(conselho.chave);
    const erro = errors[conselho.chave];

    return (
      <Grid
        key={conselho.chave}
        size={{
          xs: 12,
          md: 4
        }}>
        <Controller
          name={conselho.chave}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label={conselho.rotulo}
              fullWidth
              required={obrigatorio}
              placeholder={ehMtb ? 'Registro Ministério do Trabalho' : '123456'}
              error={!!erro}
              helperText={
                erro?.message ??
                (ehMtb ? undefined : 'Somente o número; a UF vai em "UF do Conselho".')
              }
            />
          )}
        />
      </Grid>
    );
  };

  // Get fields to validate for current tab
  const getTabFields = (tabIndex: number): (keyof CreateProfessionalRequest)[] => {
    switch (tabIndex) {
      // O tipo é validado AQUI, e não na aba de registros, porque é aqui que ele
      // é preenchido: barrar por um campo que ficou na aba anterior manda o
      // usuário procurar o que corrigir.
      case 0: // Dados Básicos
        return ['name', 'email', 'cpf', 'password', 'whatsapp', 'professionalType', 'defaultAppointmentDuration'];
      case 1: // Registros Profissionais
        return ['crm', 'rqe', 'crea', 'cro', 'cra', 'mtb', 'crfa', 'crefito', 'crq', 'crp', 'coren', 'conselhoUf', 'specialty'];
      case 2: // Responsabilidades
        return ['isExaminer', 'isMedicalResponsible', 'isTechnicalResponsible', 'isEnvironmentalResponsible', 'isLegalResponsible'];
      case 3: // Vínculos com Clínicas (no form validation needed, managed via state)
        return [];
      case 4: // Dados Bancários
        return ['bankName', 'bankAgency', 'bankAccount', 'bankAccountType', 'pixKeyType', 'pixKey', 'pis', 'companyName', 'companyCnpj', 'observations'];
      default:
        return [];
    }
  };

  // Validate current tab before moving to next
  const validateCurrentTab = async (): Promise<boolean> => {
    const fieldsToValidate = getTabFields(currentTab);
    const result = await trigger(fieldsToValidate);
    return result;
  };

  // Handle next button click
  const handleNext = async () => {
    const isValid = await validateCurrentTab();
    if (!isValid) return;
    const idx = visibleSteps.findIndex((s) => s.value === currentTab);
    if (idx >= 0 && idx < visibleSteps.length - 1) {
      setCurrentTab(visibleSteps[idx + 1]!.value);
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle back button click
  const handleBack = () => {
    const idx = visibleSteps.findIndex((s) => s.value === currentTab);
    if (idx > 0) {
      setCurrentTab(visibleSteps[idx - 1]!.value);
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleFormSubmit = async (data: CreateProfessionalRequest) => {
    // Validate all tabs before final submit
    const isValid = await validateCurrentTab();
    if (!isValid) return;
    // Clean up data before sending
    const processedData: CreateProfessionalRequest = {
      ...data,
      cpf: removeCPFMask(data.cpf ?? ''),
      whatsapp: data.whatsapp ? `+55${data.whatsapp.replace(/\D/g, '')}` : undefined,
    };

    // Vínculos com clínicas só para Médico Examinador. Para os demais, NÃO enviar o campo
    // (deixa undefined) — assim o backend não roda o sync e preserva vínculos já existentes,
    // evitando desativação silenciosa ao reeditar/trocar o tipo do profissional.
    if (isExaminer) {
      processedData.providerBindings = providerBindings
        .filter(b => b.checked)
        .map(b => ({
          providerId: b.providerId,
          providerName: b.providerName,
          function: b.function,
        }));
    }

    // Remove password if empty (for editing)
    if (!processedData.password) {
      const { password: _, ...dataWithoutPassword } = processedData;
      onSubmit(dataWithoutPassword);
      return;
    }

    onSubmit(processedData);
  };

  // Handle continue/submit button click
  const handleContinue = async () => {
    const isLastStep = visibleSteps[visibleSteps.length - 1]?.value === currentTab;
    if (isLastStep) {
      // Last tab - submit the form
      await handleSubmit(handleFormSubmit)();
    } else {
      // Not last tab - go to next tab
      await handleNext();
    }
  };

  const hasAnyErrors = Object.keys(errors).length > 0;

  return (
    <Box sx={{ mt: 1 }}>
      {/* Stepper */}
      <Stepper activeStep={visibleSteps.findIndex((s) => s.value === currentTab)} sx={{ mb: 4 }}>
        {visibleSteps.map((step) => (
          <Step key={step.value}>
            <StepLabel>{step.label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {/* Error Alert */}
      {hasAnyErrors && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Por favor, corrija os erros abaixo:</Typography>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            {errors.name && <li>{errors.name.message}</li>}
            {errors.email && <li>{errors.email.message}</li>}
            {errors.cpf && <li>{errors.cpf.message}</li>}
            {errors.password && <li>{errors.password.message}</li>}
            {errors.professionalType && <li>{errors.professionalType.message}</li>}
            {errors.crm && <li>{errors.crm.message}</li>}
            {errors.crea && <li>{errors.crea.message}</li>}
            {errors.mtb && <li>{errors.mtb.message}</li>}
            {errors.conselhoUf && <li>{errors.conselhoUf.message}</li>}
          </ul>
        </Alert>
      )}
      {/* Tab 0: Dados Básicos */}
      {currentTab === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Informações Pessoais</Typography>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Nome Completo"
                      fullWidth
                      required
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Email"
                      type="email"
                      fullWidth
                      required
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="cpf"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="CPF"
                      fullWidth
                      required
                      disabled={isEditing}
                      placeholder="XXX.XXX.XXX-XX"
                      inputProps={{ maxLength: 14 }}
                      error={!!errors.cpf}
                      helperText={errors.cpf?.message || (isEditing ? 'CPF não pode ser alterado' : '')}
                      onChange={(e) => {
                        const formatted = formatCPF(e.target.value);
                        field.onChange(formatted);
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="whatsapp"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="WhatsApp"
                      fullWidth
                      placeholder="(XX) XXXXX-XXXX"
                      inputProps={{ maxLength: 15 }}
                      error={!!errors.whatsapp}
                      helperText={errors.whatsapp?.message}
                      onChange={(e) => {
                        const formatted = formatPhone(e.target.value);
                        field.onChange(formatted);
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={isEditing ? 'Nova Senha (deixe em branco para manter)' : 'Senha'}
                      type="password"
                      fullWidth
                      required={!isEditing}
                      error={!!errors.password}
                      helperText={errors.password?.message}
                    />
                  )}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                {/*
                  O campo abre VAZIO quando o cadastro não tem tipo, e não num tipo
                  qualquer: desde a #423 é ele que sai impresso embaixo da assinatura,
                  então um padrão escolhido pelo sistema vira credencial errada em
                  documento legal para quem salvar a tela sem olhar aqui.
                */}
                <Controller
                  name="professionalType"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth required error={!!errors.professionalType}>
                      <InputLabel id="professional-type-label" shrink>Tipo de Profissional</InputLabel>
                      <Select
                        {...field}
                        labelId="professional-type-label"
                        label="Tipo de Profissional"
                        displayEmpty
                        notched
                      >
                        <MenuItem value="">
                          <em>Selecione o tipo</em>
                        </MenuItem>
                        {Object.values(ProfessionalType).map((type) => (
                          <MenuItem key={type} value={type}>
                            {PROFESSIONAL_TYPE_LABELS[type]}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.professionalType && (
                        <FormHelperText>{errors.professionalType?.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="specialty"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Especialidade"
                      fullWidth
                      placeholder="Ex: Cardiologia, Medicina do Trabalho"
                      error={!!errors.specialty}
                      helperText={errors.specialty?.message}
                    />
                  )}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="defaultAppointmentDuration"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel id="default-duration-label" shrink>Tempo Médio de Atendimento</InputLabel>
                      <Select
                        {...field}
                        labelId="default-duration-label"
                        value={field.value ?? ''}
                        label="Tempo Médio de Atendimento"
                        displayEmpty
                        notched
                      >
                        <MenuItem value="">
                          <em>Usar padrão da clínica</em>
                        </MenuItem>
                        <MenuItem value={15}>15 min</MenuItem>
                        <MenuItem value={20}>20 min</MenuItem>
                        <MenuItem value={25}>25 min</MenuItem>
                        <MenuItem value={30}>30 min</MenuItem>
                        <MenuItem value={40}>40 min</MenuItem>
                        <MenuItem value={45}>45 min</MenuItem>
                        <MenuItem value={60}>1h</MenuItem>
                        <MenuItem value={90}>1h30</MenuItem>
                        <MenuItem value={120}>2h</MenuItem>
                      </Select>
                      <FormHelperText>
                        Opcional. Se não definido, usa o padrão da clínica ao gerar escalas.
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid size={12}>
                <Controller
                  name="observations"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Observações"
                      fullWidth
                      multiline
                      rows={3}
                      error={!!errors.observations}
                      helperText={errors.observations?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
      {/* Tab 1: Registros Profissionais */}
      {currentTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Registros Profissionais</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              {conselhoDestacado
                ? `O registro no ${conselhoDestacado.rotulo} é o do tipo selecionado (${PROFESSIONAL_TYPE_LABELS[watchProfessionalType as ProfessionalType]}). Se o profissional também for inscrito em outro conselho, abra "Outro conselho".`
                : 'Este tipo não tem um conselho fixo. Informe o registro do conselho em que o profissional é inscrito.'}
            </Alert>
            <Grid container spacing={2}>
              {/* O conselho do TIPO selecionado, em destaque. Os outros nove
                  ficam recolhidos logo abaixo — disponíveis, sem competir com
                  este pela atenção de quem preenche (em geral, o
                  administrativo, que não sabe qual conselho é o do tipo). */}
              {conselhoDestacado && (
                <Grid size={12}>
                  <Box
                    role="group"
                    aria-label={`Conselho do tipo ${PROFESSIONAL_TYPE_LABELS[watchProfessionalType as ProfessionalType]}`}
                    sx={{
                      p: 2,
                      mb: 1,
                      border: 1,
                      borderColor: 'primary.main',
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Conselho de {PROFESSIONAL_TYPE_LABELS[watchProfessionalType as ProfessionalType]}
                    </Typography>
                    <Grid container spacing={2}>
                      {renderCampoDeConselho(conselhoDestacado)}
                      {showCrmFields && (
                        <Grid
                          size={{
                            xs: 12,
                            md: 4
                          }}>
                          <Controller
                            name="rqe"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="RQE"
                                fullWidth
                                placeholder="Registro de Qualificação de Especialista"
                                error={!!errors.rqe}
                                helperText={errors.rqe?.message}
                              />
                            )}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Grid>
              )}

              <Grid size={12}>
                <Accordion
                  id="outros-conselhos"
                  expanded={outrosConselhosAbertos}
                  onChange={(_, aberto) => setOutrosConselhosAlternados(aberto)}
                  disableGutters
                  elevation={0}
                  sx={{ border: 1, borderColor: 'divider', '&::before': { display: 'none' } }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    id="outros-conselhos-header"
                    aria-controls="outros-conselhos-content"
                  >
                    <Typography variant="subtitle2">Outro conselho</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Preencha aqui se o profissional for inscrito em um conselho
                      diferente do que o tipo dele indica — é o caso, por exemplo,
                      do ergonomista que é engenheiro.
                    </Typography>
                    <Grid container spacing={2}>
                      {outrosConselhos.map(renderCampoDeConselho)}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* A UF do conselho mora AQUI, e só aqui.
                  Antes ela era pedida duas vezes: neste seletor e dentro de cada
                  número de conselho (placeholder `XXXXXX/UF`, helper "123456/SP",
                  com o CRM até formatando sozinho). Quem desempata é o backend:
                  `ProfessionalRegistrationResolver.montar` imprime o registro
                  como `${numero}/${uf}` — número com UF embutida sai duplicado
                  no documento. Além disso o seletor tem vocabulário fechado,
                  contra erro de digitação.

                  LIMITAÇÃO conhecida: `conselhoUf` é UM campo para até dez
                  registros. Quem tem CRM/SP e CRP/RJ não consegue expressar os
                  dois — separar exigiria uma UF por conselho no `meta`, ou seja,
                  mudança de contrato com o backend. O helper abaixo ao menos
                  deixa a regra explícita em vez de deixar o usuário supor. */}
              {showConselhoUf && (
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <Controller
                    name="conselhoUf"
                    control={control}
                    render={({ field }) => (
                      <FormControl
                        fullWidth
                        required={showCrmFields || showCreaFields}
                        error={!!errors.conselhoUf}
                      >
                        <InputLabel id="conselho-uf-label">UF do Conselho</InputLabel>
                        <Select {...field} labelId="conselho-uf-label" label="UF do Conselho">
                          <MenuItem value="">Selecione</MenuItem>
                          {['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'].map((uf) => (
                            <MenuItem key={uf} value={uf}>{uf}</MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {errors.conselhoUf?.message ??
                            'A UF vale para todos os registros informados.'}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
              )}

              {/* Company data for autonomous */}
              <Grid size={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>
                  Dados Empresariais (para profissionais autônomos)
                </Typography>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="companyName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Razão Social"
                      fullWidth
                      error={!!errors.companyName}
                      helperText={errors.companyName?.message}
                    />
                  )}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="companyCnpj"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="CNPJ"
                      fullWidth
                      placeholder="XX.XXX.XXX/XXXX-XX"
                      error={!!errors.companyCnpj}
                      helperText={errors.companyCnpj?.message || 'Formato: 00.000.000/0000-00'}
                      onChange={(e) => field.onChange(formatCNPJ(e.target.value))}
                      inputProps={{ maxLength: 18 }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
      {/* Tab 2: Responsabilidades */}
      {currentTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Responsabilidades</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Marque as responsabilidades que este profissional exercerá no sistema.
            </Alert>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="isExaminer"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value || false} />}
                      label="Médico Examinador"
                    />
                  )}
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Realiza exames ocupacionais
                </Typography>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="isMedicalResponsible"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value || false} />}
                      label="Médico Responsável"
                    />
                  )}
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Responsável pelo Programa de Controle Médico
                </Typography>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="isTechnicalResponsible"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value || false} />}
                      label="Responsável Técnico"
                    />
                  )}
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Levantamentos técnicos dos programas de SST
                </Typography>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="isEnvironmentalResponsible"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value || false} />}
                      label="Responsável Ambiental"
                    />
                  )}
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Avaliações ambientais e laudos técnicos
                </Typography>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="isLegalResponsible"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value || false} />}
                      label="Responsável Legal"
                    />
                  )}
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Representante legal da empresa
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
      {/* Tab 3: Vínculos com Clínicas (apenas Médico Examinador) */}
      {currentTab === 3 && isExaminer && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Vínculos com Clínicas</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Selecione as clínicas onde este profissional atenderá e a função exercida em cada uma.
            </Alert>
            {providersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : providerBindings.length === 0 ? (
              <Alert severity="warning">
                Nenhuma clínica disponível. Cadastre clínicas primeiro.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {providerBindings.map((binding) => {
                  const provider = availableProviders.find(p => p.id === binding.providerId);
                  const displayName = binding.providerName || provider?.tradeName || provider?.legalName || `Clínica #${binding.providerId}`;
                  return (
                    <Grid key={binding.providerId} size={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 2,
                          border: '1px solid',
                          borderColor: binding.checked ? 'primary.main' : 'divider',
                          borderRadius: 1,
                          bgcolor: binding.checked ? 'action.selected' : 'transparent',
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={binding.checked}
                              onChange={(e) => {
                                setProviderBindings(prev =>
                                  prev.map(b =>
                                    b.providerId === binding.providerId
                                      ? { ...b, checked: e.target.checked }
                                      : b
                                  )
                                );
                              }}
                            />
                          }
                          label={
                            <Typography variant="subtitle2">{displayName}</Typography>
                          }
                          sx={{ flex: 1 }}
                        />
                        {binding.checked && (
                          <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Função</InputLabel>
                            <Select
                              value={binding.function}
                              onChange={(e) => {
                                setProviderBindings(prev =>
                                  prev.map(b =>
                                    b.providerId === binding.providerId
                                      ? { ...b, function: e.target.value as string }
                                      : b
                                  )
                                );
                              }}
                              label="Função"
                            >
                              {BINDING_FUNCTION_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </CardContent>
        </Card>
      )}
      {/* Tab 4: Dados Bancários */}
      {currentTab === 4 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Dados Bancários</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Informações para pagamento de serviços profissionais.
            </Alert>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="bankName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Nome do Banco"
                      fullWidth
                      error={!!errors.bankName}
                      helperText={errors.bankName?.message}
                    />
                  )}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="bankAgency"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Agência"
                      fullWidth
                      error={!!errors.bankAgency}
                      helperText={errors.bankAgency?.message}
                    />
                  )}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="bankAccount"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Conta"
                      fullWidth
                      error={!!errors.bankAccount}
                      helperText={errors.bankAccount?.message}
                    />
                  )}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="bankAccountType"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Tipo de Conta</InputLabel>
                      <Select {...field} label="Tipo de Conta">
                        <MenuItem value="">Selecione</MenuItem>
                        <MenuItem value="corrente">Conta Corrente</MenuItem>
                        <MenuItem value="poupanca">Conta Poupança</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="pixKeyType"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Tipo de Chave PIX</InputLabel>
                      <Select {...field} label="Tipo de Chave PIX">
                        <MenuItem value="">Selecione</MenuItem>
                        <MenuItem value="cpf">CPF</MenuItem>
                        <MenuItem value="email">Email</MenuItem>
                        <MenuItem value="telefone">Telefone</MenuItem>
                        <MenuItem value="aleatoria">Chave Aleatória</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="pixKey"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Chave PIX"
                      fullWidth
                      error={!!errors.pixKey}
                      helperText={errors.pixKey?.message}
                    />
                  )}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="pis"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="PIS/PASEP"
                      fullWidth
                      error={!!errors.pis}
                      helperText={errors.pis?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, gap: 2 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          startIcon={<CancelIcon />}
          disabled={isSubmitting}
        >
          Cancelar
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {currentTab > 0 && (
            <Button
              variant="outlined"
              onClick={handleBack}
              startIcon={<BackIcon />}
              disabled={isSubmitting}
            >
              Voltar
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleContinue}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : visibleSteps[visibleSteps.length - 1]?.value === currentTab ? (
                <SaveIcon />
              ) : (
                <NextIcon />
              )
            }
            disabled={isSubmitting}
          >
            {visibleSteps[visibleSteps.length - 1]?.value === currentTab
              ? initialData
                ? 'Salvar Alterações'
                : 'Cadastrar Profissional'
              : 'Próximo'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ProfessionalForm;
